import type { DataVisibleTableMode, DataVisibleTrendConfig } from '../models/dataVisible.types'
import type { ReportTableType } from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import type { TrendAssignment } from '../service/tablePivot'

import type { ResolvedDataVisibleTrend } from './useDataVisibleReportRequest'

/** Deep-clones a resolved trend for a settled render snapshot, matching every other snapshot
 * field's own defensive copy. */
export function cloneResolvedTrend(
  trend: ResolvedDataVisibleTrend | null,
): ResolvedDataVisibleTrend | null {
  if (!trend) return null
  return {
    ...trend,
    breakdownColumn: { ...trend.breakdownColumn },
    previewColumn: { ...trend.previewColumn },
    dateDimensions: trend.dateDimensions.map((column) => ({ ...column })),
  }
}

export interface ResolvedDataVisibleTrendRequest {
  tableMode: DataVisibleTableMode
  trend: DataVisibleTrendConfig | undefined
  /** Unlike a last-resort 'table' fallback, trend is only ever chosen when truly available. */
  tablePresenterAvailable: boolean
}

/**
 * `trend_table` is a manual "Table Type" pick, resolved from whichever columns are visible
 * rather than a host-config prop — everything else keeps using the host's own `tableMode`/`trend`
 * props, so a caller can still force trend via static config.
 */
export function resolveDataVisibleTrendRequest(
  effectiveTableType: ReportTableType,
  trendAssignment: TrendAssignment | null,
  frequency: TrendTableFrequency,
  host: { tableMode: DataVisibleTableMode; trend: DataVisibleTrendConfig | undefined },
  tableFallbackAvailable: boolean,
): ResolvedDataVisibleTrendRequest {
  if (effectiveTableType !== 'trend_table' || !trendAssignment) {
    return {
      tableMode: host.tableMode,
      trend: host.trend,
      tablePresenterAvailable: effectiveTableType === 'table' && tableFallbackAvailable,
    }
  }
  return {
    tableMode: 'trend',
    trend: {
      frequency,
      previewMetric: trendAssignment.previewMetric,
      dateDimensions: trendAssignment.dateDimensions,
    },
    tablePresenterAvailable: true,
  }
}
